import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RingLoader } from 'react-spinners';
import { getToken } from '../../../../features/token/tokenSaver';
import { adminCreateCampaign } from '../../../../services/adminCreateCampaign';
import { getAdvertiserDetails } from '../../../../services/getAdvertiserDetails';
import ScreenSelector from '../../../Advertiser/AdvertiserDashboard/ScreenSelector/ScreenSelector';
import ScreenMapView from '../../../Advertiser/AdvertiserDashboard/ScreenMapView/ScreenMapView';
import {
    useCampaignCreativeUpload,
    formatDateInputValue,
    defaultCampaignStartDate,
    defaultCampaignEndDate,
} from '../../../../hooks/useCampaignCreativeUpload';
import { totalPlaysForScreens, PLAYS_PER_SCREEN_MONTH } from '../../../../utils/advertiserScreenPricing';
import CampaignCreativeFields, { CampaignCreativeModals } from './CampaignCreativeFields';
import leftChevron from '../../../../assets/images/leftChevron.png';
import '../../../Advertiser/AdvertiserDashboard/AdvertiserDashboardNewCampaign/AdvertiserDashboardNewCampaign.css';
import './AdminCreateCampaign.css';

const CREATIVE_INPUT_IDS = {
    horizontal: 'adminCreateCampaignHorizontal',
    vertical: 'adminCreateCampaignVertical',
};

export default function AdminCreateCampaign() {
    const { id: advertiserId } = useParams();
    const navigate = useNavigate();
    const token = useSelector(getToken);

    const [advertiserName, setAdvertiserName] = useState('');
    const [campaignName, setCampaignName] = useState('');
    const [selectedScreenIds, setSelectedScreenIds] = useState([]);
    const [monthlyFee, setMonthlyFee] = useState(0);
    const [feeInput, setFeeInput] = useState('');
    const [screenSelectionError, setScreenSelectionError] = useState('');
    const [storefront, setStorefront] = useState(false);
    const [activateImmediately, setActivateImmediately] = useState(true);
    const [startDate, setStartDate] = useState(() => formatDateInputValue(defaultCampaignStartDate()));
    const [endDate, setEndDate] = useState(() => formatDateInputValue(defaultCampaignEndDate(defaultCampaignStartDate())));
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [loadingAdvertiser, setLoadingAdvertiser] = useState(true);

    const {
        campaignHorizontalContent,
        campaignVerticalContent,
        creativeEditor,
        cropExporting,
        creativeFileNames,
        creativeProcessing,
        processCreativeUpload,
        handleCreativeEditorCancel,
        handleCropNext,
        handlePreviewNext,
    } = useCampaignCreativeUpload(CREATIVE_INPUT_IDS);

    useEffect(() => {
        if (!advertiserId) {
            navigate('/adminDashboard/advertisers/home');
            return;
        }
        setLoadingAdvertiser(true);
        getAdvertiserDetails(token, advertiserId)
            .then((res) => {
                const adv = res.data?.advertiser || {};
                setAdvertiserName(adv.CompanyName || `${adv.Fname || ''} ${adv.Lname || ''}`.trim() || `Advertiser #${advertiserId}`);
            })
            .catch(() => {
                setSubmitError('Unable to load advertiser details.');
            })
            .finally(() => {
                setLoadingAdvertiser(false);
            });
    }, [advertiserId, token, navigate]);

    function handleScreenSelectionChange(selection) {
        setSelectedScreenIds(selection.screenIds);
        setMonthlyFee(selection.totalUsd);
        setFeeInput(selection.totalUsd > 0 ? selection.totalUsd.toFixed(2) : '');
        setScreenSelectionError('');
        if (selection.screenIds.length > 0) {
            setScreenSelectionError('');
        }
    }

    function handleStartDateChange(value) {
        setStartDate(value);
        if (value) {
            const parsed = new Date(`${value}T00:00:00`);
            if (!Number.isNaN(parsed.getTime())) {
                setEndDate(formatDateInputValue(defaultCampaignEndDate(parsed)));
            }
        }
    }

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitError('');

        if (!selectedScreenIds.length) {
            setScreenSelectionError('Select at least one screen.');
            return;
        }
        if (!campaignHorizontalContent || !campaignVerticalContent) {
            alert('Please upload both horizontal and vertical content.');
            return;
        }

        const fee = Number(feeInput);
        if (!Number.isFinite(fee) || fee <= 0) {
            setSubmitError('Enter a valid monthly fee.');
            return;
        }
        if (!startDate || !endDate) {
            setSubmitError('Start and end dates are required.');
            return;
        }
        if (new Date(endDate) <= new Date(startDate)) {
            setSubmitError('End date must be after start date.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await adminCreateCampaign(
                advertiserId,
                {
                    campaignName,
                    screenIds: selectedScreenIds,
                    monthlyFee: fee,
                    storefront,
                    campaignHorizontalContent,
                    campaignVerticalContent,
                    subscriptionStartDate: startDate,
                    subscriptionEndDate: endDate,
                    activateImmediately,
                },
                token
            );
            const adId = response.data?.advertisementId;
            alert(activateImmediately
                ? 'Campaign created and activated.'
                : 'Campaign created and sent to the approval queue.');
            if (adId) {
                navigate('/adminDashboard/advertisers/campaignDetails', { state: { adId } });
            } else {
                navigate(`/adminDashboard/advertisers/details/${advertiserId}`);
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to create campaign.';
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    }

    if (loadingAdvertiser) {
        return (
            <div className="adminCreateCampaignPage">
                <RingLoader color="#C52F00" loading size={40} />
            </div>
        );
    }

    return (
        <div className={`adminCreateCampaignPage ${creativeProcessing ? 'newCampaignFormBlocked' : ''}`}>
            <button type="button" className="adminCreateCampaignBack" onClick={() => navigate(`/adminDashboard/advertisers/details/${advertiserId}`)}>
                <img src={leftChevron} alt="" />
                Back to advertiser
            </button>

            <span className="registeredScreensTitle advertiserListTitle">Create campaign for {advertiserName}</span>
            <p className="adminCreateCampaignSubtitle">Set up a campaign on behalf of this advertiser. Payment is recorded manually — no Stripe checkout.</p>

            {submitError && <p className="adminCreateCampaignError">{submitError}</p>}

            <CampaignCreativeModals
                creativeEditor={creativeEditor}
                cropExporting={cropExporting}
                onCancel={handleCreativeEditorCancel}
                onCropNext={handleCropNext}
                onPreviewNext={handlePreviewNext}
            />

            <form onSubmit={onSubmit} className={creativeProcessing ? 'newCampaignFormBlocked' : ''}>
                <ScreenSelector
                    selectedScreenIds={selectedScreenIds}
                    onSelectionChange={handleScreenSelectionChange}
                    selectionError={screenSelectionError}
                />

                {selectedScreenIds.length > 0 && (
                    <p className="adminCreateCampaignSubtitle">
                        Total guaranteed plays: {totalPlaysForScreens(selectedScreenIds.length).toLocaleString()}
                        {' '}({PLAYS_PER_SCREEN_MONTH.toLocaleString()} per screen)
                    </p>
                )}

                <div style={{ marginBottom: '2rem' }}>
                    <ScreenMapView />
                </div>

                <div className="newCampaignInputContainer">
                    <span className="newCampaignInputTitle">Campaign Name</span>
                    <input
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        required
                        type="text"
                        maxLength={50}
                        placeholder="Campaign Name"
                        className="newCampaignInputInputFull"
                    />
                </div>

                <div className="adminCreateCampaignRow">
                    <div className="newCampaignInputContainer">
                        <span className="newCampaignInputTitle">Monthly fee (USD)</span>
                        <input
                            value={feeInput}
                            onChange={(e) => setFeeInput(e.target.value)}
                            required
                            type="number"
                            min="0.01"
                            step="0.01"
                            className="newCampaignInputInputFull"
                        />
                    </div>
                    <div className="newCampaignInputContainer">
                        <span className="newCampaignInputTitle">Start date</span>
                        <input
                            value={startDate}
                            onChange={(e) => handleStartDateChange(e.target.value)}
                            required
                            type="date"
                            className="newCampaignInputInputFull"
                        />
                    </div>
                    <div className="newCampaignInputContainer">
                        <span className="newCampaignInputTitle">End date</span>
                        <input
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            required
                            type="date"
                            className="newCampaignInputInputFull"
                        />
                    </div>
                </div>

                <label className="adminCreateCampaignCheckbox">
                    <input
                        type="checkbox"
                        checked={storefront}
                        onChange={(e) => setStorefront(e.target.checked)}
                    />
                    <span>Storefront placement</span>
                </label>

                <label className="adminCreateCampaignCheckbox">
                    <input
                        type="checkbox"
                        checked={activateImmediately}
                        onChange={(e) => setActivateImmediately(e.target.checked)}
                    />
                    <span>Activate immediately (skip approval queue)</span>
                </label>

                <CampaignCreativeFields
                    inputIds={CREATIVE_INPUT_IDS}
                    creativeFileNames={creativeFileNames}
                    campaignHorizontalContent={campaignHorizontalContent}
                    campaignVerticalContent={campaignVerticalContent}
                    creativeProcessing={creativeProcessing}
                    onHorizontalChange={(e) => processCreativeUpload(e, 'horizontal')}
                    onVerticalChange={(e) => processCreativeUpload(e, 'vertical')}
                />

                <input
                    type="submit"
                    className="advertiserRegisterSubmitBtn"
                    value={submitting ? 'Creating…' : 'Create campaign'}
                    disabled={submitting || Boolean(creativeProcessing) || selectedScreenIds.length === 0}
                />
            </form>
        </div>
    );
}
